import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { GetTargetScrollTop, StickToBottom } from 'use-stick-to-bottom';
import { UserMessage } from '@/features/ai-agents/components/user-message';
import { AiChatMessage } from '@/features/ai-agents/shared/models';
import { useChatSession } from '@/features/ai-agents/chat/context';
import { AgentMessage } from '@/features/ai-agents/components/agent-message';
import { VSpace } from '@/components/spacing';
import { ScrollToButtons } from '@/features/ai-agents/chat/scroll-buttons';
import Portal from '@/components/portal';
import { CHAT_INPUT_FLOATING_CONTAINER } from '@/features/ai-agents/chat/chat-area';

export interface ChatMessagesProps {
  showReasoning?: boolean;
  showTools?: boolean;

  isLocalHost?: boolean;

  inputHeight?: number;

  onScrollStateChange?: (isAtBottom: boolean, isAtTop: boolean) => void;

  emptyMessage?: string;
}

const SMALLEST_INPUT_HEIGHT = 190;
export const CHAT_MESSAGES_WRAPPER_ID = `chat-messages-wrapper`;
const LAST_MESSAGE_ITEM_SELECTOR = `.last-message-item`;

const scrollToLastMessageTop: GetTargetScrollTop = (
  defaultTargetScrollTop,
  { scrollElement, contentElement },
) => {
  const lastMessageElement = contentElement.querySelector<HTMLElement>(
    LAST_MESSAGE_ITEM_SELECTOR,
  );
  if (!lastMessageElement) return defaultTargetScrollTop;

  const scrollRect = scrollElement.getBoundingClientRect();
  const lastMessageRect = lastMessageElement.getBoundingClientRect();

  return scrollElement.scrollTop + (lastMessageRect.top - scrollRect.top);
};

const isLastMessageQuiz = (msgs: AiChatMessage[]): boolean => {
  if (!msgs.length) return false;
  const last = msgs[msgs.length - 1];
  if (last.role !== `agent`) return false;
  try {
    return last?.content?.includes(`vsly/json/quiz`);
  } catch {
    return false;
  }
};
export function ChatMessages({
  showTools,
  showReasoning,
  inputHeight = 0,
  onScrollStateChange,
  emptyMessage,
}: ChatMessagesProps) {
  const {
    messages,
    lastMessageStream,
    processedEvents,
    isProcessing,
    retryLastMessage,
    isLoadingSession,
  } = useChatSession();

  const hasInitialScrolled = useRef(false);

  useEffect(() => {
    if (hasInitialScrolled.current) return;
    if (isLoadingSession.value) return;
    if (!messages.value.length) return;
    if (!isLastMessageQuiz(messages.value)) return;
    hasInitialScrolled.current = true;

    setTimeout(() => {
      const quizElements = document.querySelectorAll(`.ai-chat-quiz`);
      if (!quizElements.length) return;
      const lastQuiz = quizElements[quizElements.length - 1] as HTMLElement;
      (
        lastQuiz?.parentNode?.parentNode?.parentNode?.parentNode as HTMLElement
      )?.scrollIntoView({
        block: `start`,
        behavior: `smooth`,
      });
    }, 1500);
  }, [isLoadingSession.value, messages.value.length]);

  const bottomSpacing = inputHeight - SMALLEST_INPUT_HEIGHT;

  return (
    <Wrapper id={CHAT_MESSAGES_WRAPPER_ID}>
      <StyledStickToBottom
        resize="smooth"
        initial="smooth"
        targetScrollTop={scrollToLastMessageTop}
      >
        {/* @ts-ignore */}
        <StickToBottom.Content className="content">
          <MessagesWrapper
            bottomPadding={bottomSpacing > 0 ? bottomSpacing : 0}
          >
            <VSpace value={0.1} />
            {messages?.value?.length === 0 &&
              !isProcessing.value &&
              !!emptyMessage && (
                <AgentMessage
                  key="empty-message"
                  message={{
                    id: `empty-message`,
                    role: `agent`,
                    content: emptyMessage,
                    created_at: new Date().getTime() / 1000,
                  }}
                />
              )}
            {messages?.value?.map((msg, index) => {
              const isLatestAndProcessing =
                isProcessing.value && index === messages.value.length - 1;
              const isLatestMessage = index === messages.value.length - 1;
              return (
                <MessageItem
                  key={messageKey(msg, index)}
                  className={isLatestMessage ? `last-message-item` : ``}
                >
                  {msg.role === `user` ? (
                    <UserMessage message={msg} />
                  ) : (
                    <AgentMessage
                      className={`${isLatestMessage ? `last-message` : ``} ${
                        isLatestAndProcessing ? `last-message-processing` : ``
                      } ${isProcessing.value ? `processing` : ``}`}
                      message={msg}
                      isLatestMessage={isLatestMessage}
                      isStreamingMessage={isLatestAndProcessing}
                      streamedMessage={
                        isLatestAndProcessing ? lastMessageStream : undefined
                      }
                      showReasoning={showReasoning}
                      showTools={showTools}
                      isProcessing={isProcessing}
                      processedEvents={
                        isLatestAndProcessing ? processedEvents : undefined
                      }
                      onRetry={isLatestMessage ? retryLastMessage : undefined}
                    />
                  )}
                </MessageItem>
              );
            })}
            <div className="end-anchor" />
            <VSpace value={12} />
          </MessagesWrapper>
        </StickToBottom.Content>
        <Portal selector={`#${CHAT_INPUT_FLOATING_CONTAINER}`}>
          <ScrollToButtons onChange={onScrollStateChange} />
        </Portal>
      </StyledStickToBottom>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const StyledStickToBottom = styled(StickToBottom)`
  && {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
    flex: 1;
    min-height: 0;
    overflow: hidden;

    .content {
      min-width: 0;
    }
  }
`;

export function messageKey(message: AiChatMessage, index: number): string {
  return `${message.role}-${message.created_at}-${index}`;
}

const MessagesWrapper = styled.div<{ bottomPadding: number }>`
  display: flex;
  flex-direction: column;
  gap: 4rem;
  width: 100%;
  height: auto;
  padding-bottom: ${(p) => p.bottomPadding}px;
`;

const MessageItem = styled.div`
  width: 100%;
`;
